import type { Automation, AutomationStep, UpsertAutomation } from '@dnx/contracts';
import { Badge, Button, Card, EmptyState, PageHeader, Spinner, Switch } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Clock, MessageSquareText, Plus, Shuffle, Tag, TrendingUp, Workflow } from 'lucide-react';
import { api } from '../lib/api.js';
import { tagsQuery } from '../reception/queries.js';
import { automationsQuery, humanWait, TEMPLATES, triggerSummary } from './shared.js';

const STEP_ICON = { send_message: MessageSquareText, wait: Clock, add_tag: Tag, move_stage: TrendingUp, assign: Shuffle } as const;

function StepTrail({ steps }: { steps: AutomationStep[] }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-[12px] text-fg-muted">
      {steps.map((s, i) => {
        const Icon = STEP_ICON[s.type];
        return (
          <span key={i} className="inline-flex items-center gap-1">
            {i > 0 && <span aria-hidden className="text-fg-subtle">→</span>}
            <span className="inline-flex h-6 items-center gap-1 rounded-md bg-surface-muted px-1.5"><Icon className="size-3.5" />{s.type === 'wait' ? humanWait(s.minutes) : ''}</span>
          </span>
        );
      })}
    </span>
  );
}

function Row({ a, tagName }: { a: Automation; tagName: (id: string) => string }) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api<Automation>(`/automations/${a.id}`, { method: 'PATCH', body: { ...toBody(a), enabled } satisfies UpsertAutomation }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['automations'] }),
  });
  return (
    <Card className="flex flex-wrap items-center gap-x-6 gap-y-3 p-5">
      <Link to="/automacoes/$id" params={{ id: a.id }} className="min-w-[260px] flex-1">
        <p className="font-semibold text-fg hover:text-primary">{a.name}</p>
        <p className="mt-0.5 text-[13px] text-fg-muted">{triggerSummary(a, tagName)}</p>
        <div className="mt-2"><StepTrail steps={a.steps} /></div>
      </Link>
      <div className="flex flex-wrap gap-1.5 text-[12px]">
        {a.stats.running > 0 && <Badge tone="primary">{a.stats.running} em andamento</Badge>}
        <Badge tone="success">{a.stats.done} concluídas</Badge>
        {a.stats.cancelled > 0 && <Badge>{a.stats.cancelled} interrompidas</Badge>}
        {a.stats.failed > 0 && <Badge tone="danger">{a.stats.failed} com erro</Badge>}
      </div>
      <div className="w-44"><Switch checked={a.enabled} onChange={(v) => toggle.mutate(v)} label={a.enabled ? 'Ligada' : 'Desligada'} disabled={toggle.isPending} /></div>
    </Card>
  );
}

export const toBody = (a: Automation): UpsertAutomation => ({
  name: a.name, enabled: a.enabled, trigger: a.trigger, triggerValue: a.triggerValue, conditions: a.conditions, steps: a.steps, stopOnReply: a.stopOnReply,
});

/** Automações: "quando isso acontecer, faça aquilo" — mensagens, esperas, etiquetas, etapa e responsável. */
export function AutomationsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery(automationsQuery);
  const { data: tags } = useQuery(tagsQuery);
  const tagName = (id: string) => tags?.find((t) => t.id === id)?.name ?? 'etiqueta';
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Automações" description="Tarefas que o sistema faz sozinho: mensagem de boas-vindas, follow-up de quem não respondeu, etiqueta e responsável conforme a etapa."
        actions={<Button onClick={() => void navigate({ to: '/automacoes/$id', params: { id: 'nova' } })}><Plus className="size-4" />Nova automação</Button>} />
      {isLoading ? <Spinner className="mt-10 size-5 text-fg-subtle" label="Carregando" /> : data?.length ? (
        <div className="mt-8 grid gap-3">{data.map((a) => <Row key={a.id} a={a} tagName={tagName} />)}</div>
      ) : (
        <EmptyState className="mt-8" icon={<Workflow className="size-6" />} title="Comece por um modelo">
          <p>Ajuste os textos e ligue. Uma sequência de follow-up para sozinha quando o paciente responde, quando o lead é fechado ou quando ele pede para sair (SAIR).</p>
        </EmptyState>
      )}
      <p className="mt-10 text-[11px] font-semibold tracking-[0.2em] text-fg-subtle uppercase">Modelos</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {TEMPLATES.map((t, i) => (
          <button key={t.title} type="button" onClick={() => void navigate({ to: '/automacoes/$id', params: { id: 'nova' }, search: { modelo: i } })}
            className="rounded-2xl border border-border bg-surface/60 p-5 text-left transition-colors hover:border-border-strong">
            <p className="font-semibold text-fg">{t.title}</p>
            <p className="mt-1 text-[13px] text-fg-muted">{t.description}</p>
            <div className="mt-3"><StepTrail steps={t.value.steps} /></div>
          </button>
        ))}
      </div>
    </div>
  );
}
